<script setup lang="ts">
/**
 * Bandeau permanent affiche au-dessus de l'app quand l'utilisateur est
 * connecte en mode demo. Permet de :
 *  - lui rappeler que c'est une demo (donnees fictives)
 *  - lui donner un bouton "Quitter" qui logout + retour /demo
 *
 * Visible uniquement si auth.isDemo === true.
 */
import { useI18n } from 'vue-i18n';
import { Sparkles, X } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { useNotificationsStore } from '../stores/notifications';

const { t } = useI18n();
const auth = useAuthStore();
const notifs = useNotificationsStore();

function quitDemo(): void {
  notifs.stopPolling();
  notifs.reset();
  auth.logout();
  // Navigation "dure" (pas router.push) vers la page de switch des roles.
  // Un rechargement complet annule toute requete en vol : sans ca, une requete
  // de rafraichissement qui repart sans token (cas de la vue Pointage de
  // l'ouvrier qui poll en continu) renvoie un 401, et l'intercepteur de
  // api/client.ts force alors window.location = '/login', ce qui ecrasait la
  // navigation vers /demo. Resultat : l'ouvrier atterrissait sur la connexion
  // au lieu de la page de switch comme les autres roles. Le hard reload aborte
  // ces requetes => plus de 401 parasite, atterrissage propre sur /demo.
  window.location.href = '/demo';
}
</script>

<template>
  <div
    v-if="auth.isDemo"
    class="bg-warning/95 text-warning-fg backdrop-blur-sm border-b border-warning/50"
    role="status"
    :aria-label="t('demo.banner.aria')"
  >
    <div class="max-w-6xl mx-auto px-3 sm:px-6 py-2 flex items-center justify-between gap-3 text-sm">
      <div class="flex items-center gap-2 min-w-0">
        <Sparkles :size="14" class="shrink-0" aria-hidden="true" />
        <span class="font-medium">{{ t('demo.banner.text') }}</span>
      </div>
      <button
        type="button"
        class="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium hover:bg-warning-fg/10 transition-colors shrink-0"
        @click="quitDemo"
      >
        <X :size="12" aria-hidden="true" />
        <span>{{ t('demo.banner.quit') }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Fallback couleur si tokens design system n'incluent pas warning-fg */
.bg-warning\/95 {
  background-color: rgba(234, 179, 8, 0.95);
}
.text-warning-fg {
  color: #422006;
}
</style>
